// BakeoffBadge — compact chip that appears in the loan header ONLY when the
// current loan program is NOT the best-qualifying agency for this borrower.
// Clicking scrolls to Loan Center where the full ProgramBakeoffCard lives.
//
// Data source: URLA current_employment[].monthly_gross → POST /api/income/calculate-all-programs
import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../api';
import { Trophy } from 'lucide-react';
import { splitBakeoffIncome } from './bakeoffIncome';

const AGENCY_LABEL = {
  fannie: 'Fannie Mae',
  freddie: 'Freddie Mac',
  fha: 'FHA',
  va: 'VA',
  usda: 'USDA',
};

// Map loan.loan_program → the comparison-key that represents it.
function currentAgencyKey(loanProgram) {
  return {
    conventional: 'fannie',
    fannie: 'fannie',
    freddie: 'freddie',
    fha: 'fha',
    va: 'va',
    usda: 'usda',
  }[(loanProgram || '').toLowerCase()] || null;
}

const fmt$ = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export function BakeoffBadge({ loan, onOpen }) {
  const [data, setData] = useState(null);

  // Same W-2 base as the Loan Center card, so the two never disagree.
  const { annualBase } = useMemo(() => splitBakeoffIncome(loan.urla?.current_employment), [loan]);

  useEffect(() => {
    if (!annualBase || annualBase <= 0) { setData(null); return; }
    let stop = false;
    api.post('/income/calculate-all-programs', { income_type: 'w2_salary', y2_gross: annualBase })
      .then(({ data }) => { if (!stop) setData(data); })
      .catch(() => { if (!stop) setData(null); });
    return () => { stop = true; };
  }, [annualBase]);

  // Hide the badge entirely unless we have a meaningful nudge to show.
  if (!data || !data.best_program) return null;

  const currentKey = currentAgencyKey(loan.loan_program);
  if (!currentKey) return null;                           // unknown/non-agency program → skip
  if (currentKey === data.best_program) return null;      // already on best program → no nudge

  const currentRow = data.results.find(r => r.program === currentKey);
  const bestQm = data.best_qualifying_monthly || 0;
  const currentQm = currentRow?.qualifying_monthly || 0;
  const delta = bestQm - currentQm;
  if (delta <= 0) return null;                            // safety: no positive gain to advertise

  const bestLabel = AGENCY_LABEL[data.best_program] || data.best_program.toUpperCase();

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="header-bakeoff-badge"
      title={`Switching to ${bestLabel} would gain ${fmt$(delta)}/mo`}
      className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[11px] font-semibold transition hover:brightness-110"
      style={{
        background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
        color: '#92400E',
        border: '1px solid #F59E0B',
        boxShadow: '0 0 0 3px rgba(245,158,11,0.08)',
      }}
    >
      <Trophy size={12} />
      <span>{bestLabel} gains <b>{fmt$(delta)}/mo</b></span>
    </button>
  );
}
